'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';

import { css, cx } from '@/styled-system/css';
import { Spinner, Text } from '@zillow/constellation-10';
import type { SearchListingsRequest, SearchListingsResult } from '@/src/lib/api';
import { useSearchListings } from '@/src/lib/hooks/query';
import { useSrpLiveSearchRequest } from './useSrpLiveSearchRequest';

const countTextClass = css({
  minWidth: 0,
  whiteSpace: 'nowrap',
});

type Props = {
  initialListingsData: SearchListingsResult | null;
  initialParams: SearchListingsRequest;
  areaId: string;
  page: number;
  propertyType: string;
};

const loadingClass = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'tightest',
  minWidth: 0,
  whiteSpace: 'nowrap',
});

function LoadingCount({ className }: { className?: string }) {
  const t = useTranslations('search');
  return (
    <Text asChild textStyle="body-sm">
      <span className={cx(loadingClass, className)}>
        <Spinner title={t('loadingRentalListingsAria')} />
        {t('loadingListings')}
      </span>
    </Text>
  );
}

function CountText({
  totalRentals,
  propertyType,
  className,
}: {
  totalRentals: number;
  propertyType: string;
  className?: string;
}) {
  const t = useTranslations('search');
  if (totalRentals <= 0) {
    return (
      <Text textStyle="body-sm" color="text.subtle">
        {t('noPropertyTypeFound', { propertyType: propertyType.toLowerCase() })}
      </Text>
    );
  }

  return (
    <Text asChild textStyle="body-sm">
      <span data-testid="srp-count" className={cx(countTextClass, className)}>
        {totalRentals.toLocaleString()} {t('rentalsAvailableLabel')}
      </span>
    </Text>
  );
}

/**
 * Live counts line. Reads the same live V3 search query LiveListingsGrid drives,
 * so the spinner shows during pan/zoom/filter refetches, not just initial SSR.
 */
export function CountsLine({ initialListingsData, initialParams, areaId, page, propertyType }: Props) {
  const [hasHydrated, setHasHydrated] = useState(false);

  useEffect(() => {
    setHasHydrated(true);
  }, []);

  const liveParams = useSrpLiveSearchRequest({ initialParams, areaId, page });
  const isInitialParams = liveParams === initialParams;

  const { data, isFetching, isPlaceholderData } = useSearchListings(liveParams, {
    refetchOnMount: false,
    initialData: isInitialParams ? (initialListingsData ?? undefined) : undefined,
  });

  const effectiveData = isInitialParams ? (data ?? initialListingsData ?? undefined) : (data ?? undefined);
  const totalRentals = effectiveData?.listings?.numUnits ?? effectiveData?.listings?.numBuildingsAvailable ?? 0;
  const isWaitingForLiveParamsData = !isInitialParams && (isPlaceholderData || data === undefined);

  // Only show the skeleton before we have any usable count. Once data has
  // landed (from SSR seed or a prior fetch), keep the number visible while
  // a background refetch runs — otherwise the map settling on its first
  // idle triggers a skeleton flash on top of the already-resolved count.
  const hasUsableData = effectiveData !== undefined;

  if (isWaitingForLiveParamsData) {
    return <LoadingCount />;
  }

  if (hasHydrated && (isFetching || isPlaceholderData) && !hasUsableData) {
    return <LoadingCount />;
  }

  return <CountText totalRentals={totalRentals} propertyType={propertyType} />;
}
